import {
  Box,
  Flex,
  Heading,
  Radio,
  RadioGroup,
  Stack,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import {
  Controller,
  FormProvider,
  SubmitHandler,
  UseFormReturn,
  useWatch,
} from "react-hook-form";

import { Button, FormControl } from "../../../components";
import { CreateLoading } from "./CreateLoading";
import { SetupFormValues } from "./types";

type AiInterviewerJdSubStepProps = {
  methods: UseFormReturn<SetupFormValues>;
  onSubmit: SubmitHandler<SetupFormValues>;
  onBack: () => void;
  loading: boolean;
};

export const AiInterviewerJdSubStep: React.FC<AiInterviewerJdSubStepProps> = ({
  methods,
  onSubmit,
  onBack,
  loading,
}) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = methods;

  const autoCreateGuide = useWatch({ control, name: "autoCreateGuide" });

  return (
    <FormProvider {...methods}>
      <Box w="100%" overflow="auto">
        <Flex
          direction="column"
          align="center"
          maxWidth="692px"
          p="12"
          my="8"
          mx="auto"
          gap="12"
          borderRadius="xl"
          boxShadow="base"
          border="1px solid"
          borderColor="blue.200"
          bg="white"
        >
          {loading ? (
            <CreateLoading autoCreateGuide={autoCreateGuide} />
          ) : (
            <>
              <Heading fontSize="3xl" fontWeight="medium">
                Job description
              </Heading>
              <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
                <VStack spacing={6} align="stretch" w="100%">
                  <FormControl
                    error={errors.jobDescription}
                    displayName="Add a job description or tell us more about the position"
                    helpText="Used to generate interview questions and answer candidate questions"
                    helpTextProps={{ fontSize: "xs" }}
                  >
                    <Textarea
                      {...register("jobDescription", {
                        required: "Job description is required",
                        validate: (value) =>
                          value.trim().length > 0 ||
                          "Job description cannot be empty",
                      })}
                      placeholder=""
                      fontSize="sm"
                      borderRadius="md"
                      w="100%"
                      rows={4}
                    />
                  </FormControl>
                  <FormControl displayName="Should we create interview questions & evaluation criteria from your JD?">
                    <Controller
                      name="autoCreateGuide"
                      control={control}
                      render={({ field }) => (
                        <RadioGroup
                          value={field.value ? "yes" : "no"}
                          onChange={(val) => field.onChange(val === "yes")}
                        >
                          <Stack spacing={3}>
                            <Radio value="yes">
                              Yes, create an interview guide for me to review.
                            </Radio>
                            <Radio value="no">
                              No, I want to start with a blank interview guide
                              that I&apos;ll complete.
                            </Radio>
                          </Stack>
                        </RadioGroup>
                      )}
                    />
                  </FormControl>
                  {autoCreateGuide && (
                    <FormControl displayName="Optional position context or instructions">
                      <Textarea
                        {...register("guideContext")}
                        placeholder="What else should we know to build the interview questions and evaluation criteria? Add more details the role, specific topics or competencies to include, or other instructions."
                        _placeholder={{ color: "textPlaceholderDefault" }}
                        fontSize="sm"
                        borderRadius="md"
                        w="100%"
                        rows={3}
                      />
                    </FormControl>
                  )}
                </VStack>

                <Flex w="100%" justify="space-between" mt={8}>
                  <Button variant="ghost" type="button" onClick={onBack}>
                    Back
                  </Button>
                  <Button colorScheme="blue" type="submit">
                    Next: Interview guide
                  </Button>
                </Flex>
              </form>
            </>
          )}
        </Flex>
      </Box>
    </FormProvider>
  );
};
